// core/router.js — hash router mínimo.
//
// Contexto: o monólito v18 alternava telas via display:none/block em funções
// showPortal()/showLogin()/showOratoria()/etc (legacy linhas 5454-5520+).
// Não havia URL — botão voltar do navegador não funcionava, deep link impossível.
//
// Esta versão introduz rotas com hash (`#/admin/frequencia`) mantendo 100%
// de compatibilidade com apps estáticos (nenhum servidor precisa configurar
// fallback para SPA, importante porque o deploy futuro pode ser GitHub Pages
// ou S3 estático).
//
// Rotas planejadas (serão registradas pelos módulos das Fases 5 e 6):
//   #/                      → splash / tela de boas-vindas
//   #/login                 → PIN modal (aluno ou admin)
//   #/aluno                 → portal do aluno
//   #/aluno/frequencia      → minha frequência
//   #/aluno/oratoria        → portal oratória
//   #/aluno/financeiro      → financeiro
//   #/aluno/materiais       → materiais
//   #/admin                 → painel admin
//   #/admin/frequencia      → grid de frequência
//   #/admin/lancar          → modal de lançamento manual (Fase 7)
//   #/admin/historico       → log de auditoria (Fase 8)
//   #/admin/financeiro      → financeiro admin
//   #/admin/conteudo        → gerenciar conteúdo
//
// Uso:
//   import { router } from './core/router.js';
//   router.register('/admin', ({params}) => renderAdminHome(params));
//   router.register('/admin/frequencia', ({params}) => renderFreq(params));
//   router.register('*', () => render404());        // fallback
//   router.onNavigate((from, to) => console.log(from, '→', to));
//   router.start();                                 // começa a ouvir hashchange
//   router.go('/admin/frequencia');                 // navegação programática
//   router.go('/admin/frequencia', { turma: '14' }); // com query
//
// Match é exato por path. Params de querystring são parseados de `?turma=14`.

const routes = new Map();       // path → handler
const listeners = new Set();    // (from, to) → void
let currentPath = null;
let started = false;

function parseHash(hash) {
  // Formato: #/path/segment?k=v&k2=v2
  const raw = (hash || '').replace(/^#/, '');
  const [path, queryStr = ''] = raw.split('?');
  const normalized = path.startsWith('/') ? path : '/' + path;
  const params = {};
  if (queryStr) {
    for (const [k, v] of new URLSearchParams(queryStr)) params[k] = v;
  }
  return { path: normalized === '/' ? '/' : normalized.replace(/\/+$/, ''), params };
}

function buildHash(path, params = {}) {
  const keys = Object.keys(params);
  const qs = keys.length ? '?' + new URLSearchParams(params).toString() : '';
  return '#' + path + qs;
}

function dispatch() {
  const { path, params } = parseHash(location.hash);
  const handler = routes.get(path) || routes.get('*');
  const from = currentPath;

  if (!handler) {
    console.warn(`[router] sem handler para "${path}" e sem rota '*'`);
  } else {
    try {
      handler({ path, params, from });
    } catch (e) {
      console.error(`[router] handler de "${path}" lançou:`, e);
    }
  }

  const to = path;
  currentPath = to;
  for (const fn of listeners) {
    try { fn(from, to, params); } catch (e) { console.error('[router] listener erro:', e); }
  }
}

function register(path, handler) {
  if (typeof handler !== 'function') throw new Error('[router] handler precisa ser função');
  routes.set(path, handler);
}

function unregister(path) {
  routes.delete(path);
}

function go(path, params = {}) {
  const target = buildHash(path, params);
  if (location.hash === target) {
    // Mesma URL: força re-dispatch (útil para "recarregar" a tela atual).
    dispatch();
  } else {
    location.hash = target;
  }
}

function onNavigate(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function start(defaultPath = '/') {
  if (started) return;
  started = true;
  window.addEventListener('hashchange', dispatch);
  // Bootstrap inicial: se a URL não tem hash, manda para default.
  if (!location.hash || location.hash === '#') {
    location.hash = buildHash(defaultPath);
  } else {
    dispatch();
  }
}

function getCurrent() {
  return parseHash(location.hash);
}

export const router = {
  register,
  unregister,
  go,
  start,
  onNavigate,
  getCurrent,
};
